/**
 * Add or edit one of the other suppliers. The code may be left blank - the
 * server then gives the next V-number (V001, V002 ...).
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import DialogShell, { FieldGrid } from "layouts/accounting/DialogShell";
import { Field, Pick, Section, asOptions } from "layouts/accounting/Fields";
import { api, problemOf, CURRENCIES } from "layouts/accounting/api";

export const KINDS = [
  { id: "local", name: "Local · محلي" },
  { id: "foreign", name: "Other country · خارجي" },
  { id: "services", name: "Services · خدمات" },
  { id: "shipping", name: "Shipping & transport · شحن ونقل" },
  { id: "other", name: "Other · آخر" },
];

const BLANK = {
  code: "",
  name: "",
  kind: "local",
  country: "",
  city: "",
  phone: "",
  contact: "",
  tax_number: "",
  currency: "ILS",
  notes: "",
  is_active: true,
};

/** A row from the server as the form holds it - text, never null. */
const formOf = (vendor) =>
  Object.fromEntries(
    Object.entries(BLANK).map(([k, v]) => [
      k,
      vendor[k] === null || vendor[k] === undefined ? v : vendor[k],
    ])
  );

function VendorDialog({ open, vendor, onClose, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(vendor ? formOf(vendor) : BLANK);
  }, [open, vendor]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!String(form.name).trim()) return setError("Give the supplier a name");
    if (!form.kind || !form.currency) return setError("Pick the kind and the currency");
    setSaving(true);
    setError("");
    try {
      if (vendor) await api.put(`/vendors/${vendor.id}`, form);
      else await api.post("/vendors", form);
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(problemOf(e, "Could not save the supplier"));
    }
    return null;
  };

  const text = (key, label, extra = {}) => (
    <Field
      label={label}
      value={form[key]}
      onChange={(e) => set({ [key]: e.target.value })}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...extra}
    />
  );

  return (
    <DialogShell
      open={open}
      title={vendor ? `Edit ${vendor.code} - ${vendor.name}` : "New Supplier · مورد جديد"}
      onClose={onClose}
      onSave={save}
      saving={saving}
      error={error}
    >
      <Section title="The supplier · المورد">
        <FieldGrid>
          {text("name", "Name · الاسم")}
          {text("code", "Code · الكود", {
            placeholder: vendor ? "" : "Blank = next V-number",
            helperText: vendor ? "Blank keeps the current code" : "Leave blank for V001, V002 ...",
          })}
        </FieldGrid>
        <FieldGrid>
          <Pick
            label="Kind · النوع"
            options={KINDS}
            value={form.kind}
            onChange={(id) => set({ kind: id })}
            getLabel={(k) => k.name}
          />
          <Pick
            label="Currency · العملة"
            options={asOptions(CURRENCIES)}
            value={form.currency}
            onChange={(id) => set({ currency: id })}
            getLabel={(c) => c.name}
          />
        </FieldGrid>
      </Section>

      <Section title="Contact · التواصل">
        <FieldGrid>
          {text("country", "Country · الدولة")}
          {text("city", "City · المدينة")}
          {text("phone", "Phone · الهاتف")}
          {text("contact", "Contact person · الشخص المسؤول")}
        </FieldGrid>
        {text("tax_number", "Tax / licence no. · الرقم الضريبي / رقم المشتغل")}
        {text("notes", "Notes · ملاحظات", { multiline: true, minRows: 2 })}
        <FormControlLabel
          sx={{ ml: 0.5 }}
          control={
            <Switch
              checked={Boolean(form.is_active)}
              onChange={(e) => set({ is_active: e.target.checked })}
            />
          }
          label="Active"
        />
      </Section>
    </DialogShell>
  );
}

VendorDialog.defaultProps = { vendor: null };
VendorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  vendor: PropTypes.shape({ id: PropTypes.number, code: PropTypes.string, name: PropTypes.string }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default VendorDialog;
